import { useMemo, useCallback, startTransition } from "react";
import styled, { keyframes, css } from "styled-components";

import SelectorSenario from "./SelectorSenario";
import { useFile } from "../Contexts";

// ── Animations ───────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(15, 58, 141, 0.35); }
  50%       { box-shadow: 0 0 0 8px rgba(15, 58, 141, 0); }
`;

// ── Styled Components ────────────────────────────────────────────────────────

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin: 24px auto 16px;
  /* width: 88%; */
  background: #fff;
  border: 1px solid #e4e8f0;
  border-radius: 16px;
  padding: 24px 32px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.05);
  overflow: visible;
`;

/* Each step is a vertical column: circle → label → badge → selector */
const StepCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 6px;
  animation: ${fadeIn} 0.3s ease both;
  animation-delay: ${({ $index }) => $index * 0.07}s;
`;

/* Connector sits between StepCards, vertically centered to the circle row */
const ConnectorWrap = styled.div`
  flex: 0 0 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Push connector down to align with circle center:
     circle height = 42px, gap = 6px → center at 21px from top of StepCard */
  padding-top: 21px;
  /* align-self: flex-start; */
`;

const ConnectorLine = styled.div`
  width: 100%;
  height: 2px;
  background: ${({ $active }) =>
    $active ? "linear-gradient(90deg, #0f3a8d, #3b82f6)" : "#e2e8f0"};
  transition: background 0.4s ease;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    right: -1px;
    top: -3px;
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
    border-left: 8px solid ${({ $active }) => ($active ? "#3b82f6" : "#e2e8f0")};
    transition: border-left-color 0.4s ease;
  }
`;

const Circle = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  flex-shrink: 0;
  transition: all 0.3s ease;

  ${({ $state }) =>
    $state === "done" &&
    css`
      background: #0f3a8d;
      color: #fff;
      box-shadow: 0 4px 16px rgba(15, 58, 141, 0.28);
    `}
  ${({ $state }) =>
    $state === "active" &&
    css`
      background: #fff;
      color: #0f3a8d;
      border: 2.5px solid #0f3a8d;
      animation: ${pulse} 2s ease infinite;
    `}
  ${({ $state }) =>
    $state === "locked" &&
    css`
      background: #f8fafc;
      color: #c8d3e0;
      border: 2px solid #e8edf3;
    `}
`;

const StepLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ $state }) =>
    $state === "locked" ? "#c8d3e0" : $state === "done" ? "#0f3a8d" : "#1e293b"};
  transition: color 0.3s ease;
`;

const Badge = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #1d4ed8;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 20px;
  padding: 2px 12px;
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BadgeSpacer = styled.div`
  height: 22px;
`;

const SelectorWrap = styled.div`
  /* width: 100%; */
  transition: opacity 0.3s ease;
  opacity: ${({ $locked }) => ($locked ? 0.32 : 1)};
  pointer-events: ${({ $locked }) => ($locked ? "none" : "auto")};
  position: relative;
  z-index: 10;
`;

// ── Helpers ──────────────────────────────────────────────────────────────────

function buildModelOptions(executedFile, results) {
  return (
    executedFile
      ?.filter(({ key, filename }) => {
        const runKey = key || filename;
        const result = results?.find((r) => r.key === runKey || r.model === runKey);
        return result?.data && Object.keys(result.data).length > 0;
      })
      .map(({ key, filename, output_format }) => {
        const runKey = key || filename;
        const fmt = output_format ?? 1;
        const cleanName = filename
          .replace(/\.[^/.]+$/, "")       // strip .xlsx
          .replace(/__fmt\d+$/i, "");      // strip __fmt1

        const label = fmt === 1
          ? cleanName
          : `${cleanName} (Format ${fmt})`;
        return { value: runKey, label };
      }) ?? []
  );
}
function buildScenarioOptions(results, pickedModel) {
  if (!results?.length || !pickedModel?.length) return [];
  const seen = new Set();
  for (const { key, model, data } of results) {
    const runKey = key || model;
    if (!pickedModel.includes(runKey) || !data) continue;
    for (const k of Object.keys(data)) seen.add(k);
  }
  return Array.from(seen, (v) => ({
    value: v,
    label: v.match(/^scenario(\d+)$/i)
      ? `Scenario ${parseInt(v.match(/^scenario(\d+)$/i)[1]) + 1}`
      : v,
  }));
}

function buildElementOptions(results, pickedModel) {
  if (!results?.length || !pickedModel?.length) return [];
  const seen = new Set();
  for (const { key, model, data } of results) {
    const runKey = key || model;
    if (!pickedModel.includes(runKey) || !data) continue;
    for (const items of Object.values(data)) {
      if (!Array.isArray(items)) continue;
      for (const { name } of items) {
        if (name) seen.add(name);
      }
    }
  }
  return Array.from(seen, (v) => ({ value: v, label: v }));
}

function summarize(picked, options) {
  if (!picked?.length) return null;
  if (picked.length === 1) {
    const opt = options.find((o) => o.value === picked[0]);
    return opt?.label ?? picked[0];
  }
  return `${picked.length} selected`;
}

function stepState(picked, locked) {
  if (locked) return "locked";
  if (picked?.length > 0) return "done";
  return "active";
}
function stripExt(filename) {
  return filename.replace(/\.[^/.]+$/, "");
}
// ── Component ────────────────────────────────────────────────────────────────

export default function Controller({
  pickedModel,
  setPickedModel,
  pickedScenario,
  setPickedScenario,
  pickedElement,
  setPickedElement,
}) {
  const { executedFile, results } = useFile();
  const modelOptions    = useMemo(() => buildModelOptions(executedFile, results), [executedFile, results]);
  const scenarioOptions = useMemo(() => buildScenarioOptions(results, pickedModel), [results, pickedModel]);
  const elementOptions  = useMemo(() => buildElementOptions(results, pickedModel), [results, pickedModel]);

  const onPickModel = useCallback((v) => {
    startTransition(() => {
      setPickedModel(v);
      setPickedScenario([]);
      setPickedElement([]);
    });
  }, [setPickedModel, setPickedScenario, setPickedElement]);

  const onPickScenario = useCallback((v) => startTransition(() => setPickedScenario(v)), [setPickedScenario]);
  const onPickElement  = useCallback((v) => startTransition(() => setPickedElement(v)),  [setPickedElement]);

  const scenarioLocked = !pickedModel?.length;
  const elementLocked  = !pickedScenario?.length;

  const modelState    = stepState(pickedModel,    false);
  const scenarioState = stepState(pickedScenario, scenarioLocked);
  const elementState  = stepState(pickedElement,  elementLocked);

  const modelBadge    = summarize(pickedModel,    modelOptions);
  const scenarioBadge = summarize(pickedScenario, scenarioOptions);
  const elementBadge  = summarize(pickedElement,  elementOptions);

  return (
    <Wrapper>

      {/* ── Step 1: Model ── */}
      <StepCard $index={0}>
        <Circle $state={modelState}>{modelState === "done" ? "✓" : "1"}</Circle>
        <StepLabel $state={modelState}>Model</StepLabel>
        {modelBadge ? <Badge title={stripExt(modelBadge)}>{stripExt(modelBadge)}</Badge> : <BadgeSpacer />}
        <SelectorWrap $locked={false}>
          <SelectorSenario
            id="model" label="model"
            value={pickedModel} onChange={onPickModel}
            placeholder="Select a model" options={modelOptions}
          />
        </SelectorWrap>
      </StepCard>

      <ConnectorWrap>
        <ConnectorLine $active={!!pickedModel?.length} />
      </ConnectorWrap>

      {/* ── Step 2: Scenarios ── */}
      <StepCard $index={1}>
        <Circle $state={scenarioState}>{scenarioState === "done" ? "✓" : "2"}</Circle>
        <StepLabel $state={scenarioState}>Scenarios</StepLabel>
        {scenarioBadge ? <Badge title={scenarioBadge}>{scenarioBadge}</Badge> : <BadgeSpacer />}
        <SelectorWrap $locked={scenarioLocked}>
          <SelectorSenario
            id="scenarios" label="scenarios"
            value={pickedScenario} onChange={onPickScenario}
            placeholder={scenarioLocked ? "Select a model first" : "Select scenarios"}
            options={scenarioOptions}
          />
        </SelectorWrap>
      </StepCard>

      <ConnectorWrap>
        <ConnectorLine $active={!!pickedScenario?.length} />
      </ConnectorWrap>

      {/* ── Step 3: Elements ── */}
      <StepCard $index={2}>
        <Circle $state={elementState}>{elementState === "done" ? "✓" : "3"}</Circle>
        <StepLabel $state={elementState}>Elements</StepLabel>
        {elementBadge ? <Badge title={elementBadge}>{elementBadge}</Badge> : <BadgeSpacer />}
        <SelectorWrap $locked={elementLocked}>
          <SelectorSenario
            id="elements" label="elements"
            value={pickedElement} onChange={onPickElement}
            placeholder={elementLocked ? "Select scenarios first" : "Select elements"}
            options={elementOptions}
          />
        </SelectorWrap>
      </StepCard>

    </Wrapper>
  );
}